import React from 'react';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import ReplayIcon from '@material-ui/icons/Replay';
import { TP } from '../../../constants/consts';
import { useTranslation } from '../../../src/i18n';
import TextButton from '../../../components/TextButton';
import { colors, fontSize } from '../../../constants/styleConstants';
import { DateRangeFilter } from '../../InteractiveFilterPanel/components/dateRangeFilter';
import { createDate } from '../../../utils/date';

const useStyles = makeStyles({
  gridRowItem: {
    margin: '10px 5px 5px 5px',
    flexGrow: 1,
  },
  noMarginTop: {
    marginTop: 0,
  },
  searchAction: {
    width: 60,
    height: 35,
    margin: 0,
    padding: '0',
    borderRadius: 8,
    boxShadow: '0 2px 4px 0 rgba(0, 31, 59, 0.25)',
    backgroundColor: colors.blue3,
    color: colors.white,
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: colors.blue3,
    },

    '&:active': {
      backgroundColor: colors.blue3,
    },

    '&:focus': {
      backgroundColor: colors.blue3,
    },
  },
  clearAllAction: {
    width: 60,
    height: 34,
    margin: '0px 8px 0 0',
    padding: '10px 2px',
    fontSize: fontSize.smallTitle,
    color: colors.darkText,

    '&:hover': {
      color: colors.darkBlue,
    },

    '&:active': {
      color: colors.darkBlue,
    },

    '&:focus': {
      color: colors.darkBlue,
    },
  },
  callToActionGroup: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
  },
  dateBox: {
    margin: '12px',
    padding: '12px 0px 12px 0px',
  },
  content: {
    borderRadius: '8px',
    border: `solid 1px ${colors.border.grey1}`,
    backgroundColor: colors.white,
    '&:hover': {
      border: `solid 1px ${colors.blue3}`,
      backgroundColor: colors.blue1,
    },
  },
  selected: {
    borderRadius: '8px',
    border: `solid 1px ${colors.blue3}`,
    backgroundColor: colors.blue1,
    '&:hover': {
      border: `solid 1px ${colors.blue3}`,
      backgroundColor: colors.blue1,
    },
    color: colors.blue3,
    fontWeight: 'bold',
  },
  customStyle: {
    textTransform: 'none',
    fontSize: fontSize.smallTitle,
    margin: '2px 4px 2px 0px',
    marginRight: '8px',
  },
  label: {
    fontWeight: 'bold',
    color: colors.darkText,
    fontSize: fontSize.medium,
  },
  seasonItem: {
    display: 'flex',
    flexWrap: 'wrap',
  },
});

const formatDate = dateToFormat => {
  if (dateToFormat instanceof Date) {
    return createDate(dateToFormat)
      .format('YYYY-MM-DD')
      .toString();
  }
  if (!isNaN(new Date(dateToFormat))) return dateToFormat;
  return '';
};
const addDays = (dt, days) => {
  const result = new Date(dt);
  result.setDate(result.getDate() + days);
  return result;
};
const today = new Date();
const getFinalQueryConditions = state => {
  let { date_from, date_to, season_id } = state;
  date_from = date_from ? formatDate(date_from) : null;
  date_to = date_to ? formatDate(date_to) : null;

  return season_id
    ? { filterType: 'date', selectedValue: { season_id } }
    : { filterType: 'date', selectedValue: { date_from, date_to } };
};

export default function DateFilter({ state, dispatch, onSubmit, handleToggle, seasonsOptions }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const i18NText = {
    today: t(`${TP}.m_TODAY`),
    tomorrow: t(`${TP}.m_TOMORROW`),
    plus7days: t(`${TP}.X_DAYS`, { days: '+7' }),
    plus14days: t(`${TP}.X_DAYS`, { days: '+14' }),
    plus30days: t(`${TP}.X_DAYS`, { days: '+30' }),
    clearAll: t(`${TP}.FORM_CLEAR_ALL`),
    search: t(`${TP}.SEARCH`),
    apply: t(`${TP}.m_APPLY`),
  };

  const getDisabledFromDays = () => {
    if (state.date_to) {
      return [{ after: new Date(state.date_to) }];
    }
    if (state.selectedValue.date_to) {
      return [{ after: new Date(state.selectedValue.date_to) }];
    }
    return [];
  };

  const getDisabledToDays = () => {
    if (state.date_from) {
      return [{ before: new Date(state.date_from) }];
    }
    if (state.selectedValue.date_from) {
      return [{ before: new Date(state.selectedValue.date_from) }];
    }
    return [];
  };

  return (
    <div className={classes.dateBox}>
      <DateRangeFilter
        dateFrom={{
          className: classnames({
            [classes.gridRowItem]: true,
          }),
          label: '',
          selectedDay: state.date_from ? state.date_from : state.selectedValue.date_from,
          disabledDays: getDisabledFromDays(),
          asTag: 'form',
          onChange: day => {
            dispatch({
              type: 'UPDATE_DATE',
              payload: {
                new_date_from: day,
                new_date_to: state.date_to || state.selectedValue.date_to,
              },
            });
          },
        }}
        dateTo={{
          label: '',
          className: classnames({
            [classes.gridRowItem]: true,
          }),
          selectedDay: state.date_to ? state.date_to : state.selectedValue.date_to,
          disabledDays: getDisabledToDays(),
          asTag: 'form',
          onChange: day => {
            dispatch({
              type: 'UPDATE_DATE',
              payload: {
                new_date_to: day,
                new_date_from: state.date_from || state.selectedValue.date_from,
              },
            });
          },
        }}
        addDays={{
          group: {
            className: classnames(classes.gridRowItem),
          },
          today: {
            label: i18NText.today,
            onChange: () => {
              dispatch({
                type: 'UPDATE_DATE',
                payload: {
                  new_date_to: today,
                  new_date_from: today,
                },
              });
            },
          },
          tomorrow: {
            label: i18NText.tomorrow,
            onChange: () => {
              dispatch({
                type: 'UPDATE_DATE',
                payload: {
                  new_date_to: addDays(today, 1),
                  new_date_from: addDays(today, 1),
                },
              });
            },
          },
          plus7: {
            label: i18NText.plus7days,
            onClick: () => {
              const new_date_to = addDays(state.date_to || today, 7);
              dispatch({
                type: 'UPDATE_DATE',
                payload: {
                  new_date_to,
                },
              });
            },
          },
          plus14: {
            label: i18NText.plus14days,
            onClick: () => {
              const new_date_to = addDays(state.date_to || today, 14);
              dispatch({
                type: 'UPDATE_DATE',
                payload: {
                  new_date_to,
                },
              });
            },
          },
          plus30: {
            label: i18NText.plus30days,
            onClick: () => {
              const new_date_to = addDays(state.date_to || today, 30);
              dispatch({
                type: 'UPDATE_DATE',
                payload: {
                  new_date_to,
                },
              });
            },
          },
        }}
      />
      {seasonsOptions?.length > 0 && (
        <>
          <p className={classes.label}>{t(`${TP}.m_SEASON`)}</p>
          <div className={classes.seasonItem}>
            {seasonsOptions?.map((item, index) => {
              return (
                <Button
                  key={`${item.label}-${index}`}
                  onClick={() => {
                    dispatch({
                      type: 'UPDATE_SEASON',
                      payload: {
                        season_id: item.value,
                      },
                    });
                  }}
                  className={classnames(
                    classes.content,
                    classes.customStyle,
                    (state.season_id || state.selectedValue.season_id) === item.value && classes.selected,
                  )}
                >
                  <span className={classes.labelSort}>{item.label}</span>
                </Button>
              );
            })}
          </div>
        </>
      )}
      <div key="call-to-action" className={classnames(classes.gridRowItem, classes.callToActionGroup)}>
        <TextButton
          className={classes.clearAllAction}
          onClick={event => {
            // eslint-disable-next-line no-unused-expressions
            dispatch({
              type: 'UPDATE_DATE',
              payload: {
                new_date_to: today,
                new_date_from: today,
              },
            });
            event.preventDefault();
          }}
        >
          <ReplayIcon></ReplayIcon>
          {i18NText.clearAll}
        </TextButton>
        <Button
          type="submit"
          onClick={() => {
            if (typeof onSubmit === 'function') {
              onSubmit(getFinalQueryConditions(state));
              handleToggle && handleToggle();
            }
          }}
          className={classnames(classes.searchAction)}
        >
          {i18NText.apply}
        </Button>
      </div>
    </div>
  );
}
